import { DetailsHeader } from "components";
import { RolePermissions, RouteNames } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import type { OutstandingDetailsHeaderOwnProps } from "components/sectionHeader/detailsHeader/types";
import { useCallback, useMemo, useState } from "react";
import { bindAction } from "interfaces";
import { fetchDraftExternalLinkEntity } from "../state/thunks/externalLinkCommandThunk";
import { useNavigate } from "react-router";
import { AddPacksButton } from "features/Licensing/Packs/AssociatedPacks/AddPacksButton";
import AddAssetsToPackModal from "components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";
import { noop } from "lodash";
import Restricted from "features/Application/Restricted";

interface EditExternalLinkHeaderProps extends OutstandingDetailsHeaderOwnProps, PropsFromRedux {
  back: () => void;
  canAddPacks: boolean;
  showAddPacksButton: boolean;
}

const EditExternalLinkHeader = ({
  back,
  fetchDraftExternalLinkEntity,
  externalLink,
  entityStateActions,
  updateInProgress,
  canAddPacks,
  showAddPacksButton,
  ...props
}: EditExternalLinkHeaderProps) => {
  const navigate = useNavigate();
  const [packsModalShown, setPacksModalShown] = useState(false);
  const onClose = useCallback(() => navigate(`/${RouteNames.contentLinks}`), [navigate]);
  const actions = useMemo(() => {
    return {
      onClose,
      onEdit: () => fetchDraftExternalLinkEntity(externalLink?.id || 0),
      onPublish: entityStateActions?.onPublish,
      onRevert: entityStateActions?.onRevert,
    };
  }, [onClose, externalLink?.id, fetchDraftExternalLinkEntity, entityStateActions]);

  const renderAddPackButton = (hasAnyPermission: boolean) => (
    <AddPacksButton disabled={!canAddPacks} hasPermission={hasAnyPermission} onClick={() => setPacksModalShown(true)} />
  );

  return (
    <DetailsHeader
      {...props}
      pageTitle={externalLink?.title || ""}
      backButton={back || `/${RouteNames.contentLinks}`}
      defaultURL={`/${RouteNames.contentLinks}`}
      titleForGA="Asset Details"
      entityStateActions={actions}
      canBePublished={!updateInProgress && props.canBePublished}
    >
      {showAddPacksButton && (
        <Restricted
          permissions={[RolePermissions.AssetsCreate, RolePermissions.PacksManage]}
          renderContent={(hasAnyPermission) => renderAddPackButton(hasAnyPermission)}
        />
      )}
      <AddAssetsToPackModal
        showModal={packsModalShown}
        onClose={() => setPacksModalShown(false)}
        selectedItemIds={[externalLink?.id || 0]}
        contentType="Link"
        onComplete={noop}
      />
    </DetailsHeader>
  );
};

const mapStateToProps = (state: RootState) => {
  const externalLinks = state.library.externalLinks.details;
  return {
    externalLink: externalLinks.link,
    error: externalLinks.error,
    isLoading: externalLinks.isLoading,
    isLoaded: externalLinks.isLoaded,
    userId: Number(state.userProfile.id),
    updateInProgress: state.library.externalLinks.entityState.isEntityCommandInProgress,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchDraftExternalLinkEntity: bindAction(fetchDraftExternalLinkEntity, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditExternalLinkHeader);

export default ConnectedComponent;
